// Dansk datoformat DD-MM-ÅÅÅÅ. Databasen gemmer ISO; kun visning ændres.
export function daDate(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return String(iso);
  return `${d}-${m}-${y}`;
}

// --- Hjælpere til Gantt / Ressourceplan ---
const DA_MONTHS = ["Januar", "Februar", "Marts", "April", "Maj", "Juni",
  "Juli", "August", "September", "Oktober", "November", "December"];

export function toDate(iso) {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function toISO(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function addDays(dt, n) {
  const r = new Date(dt);
  r.setDate(r.getDate() + n);
  return r;
}

export function diffDays(a, b) {
  return Math.round((toDate(toISO(a)) - toDate(toISO(b))) / 86400000);
}

// ISO-ugenummer (dansk uge starter mandag)
export function isoWeek(dt) {
  const d = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

export function monthLabel(dt) {
  return `${DA_MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}

export function quarterLabel(dt) {
  return `K${Math.floor(dt.getMonth() / 3) + 1} ${dt.getFullYear()}`;
}

export function startOfMonday(dt) {
  const r = new Date(dt);
  const day = r.getDay() || 7;
  if (day !== 1) r.setDate(r.getDate() - (day - 1));
  r.setHours(0, 0, 0, 0);
  return r;
}

// BSM lukkeperioder (alle år): sommerferie uge 29-31 · jul/nytår 20. dec – 3. jan
export const CLOSED_LABEL = "BSM har lukket";
export function isClosedDate(input) {
  const dt = input instanceof Date ? input : toDate(input);
  if (!dt) return false;
  const m = dt.getMonth() + 1;
  const day = dt.getDate();
  if ((m === 12 && day >= 20) || (m === 1 && day <= 3)) return true;
  const wk = isoWeek(dt);
  return wk === 29 || wk === 30 || wk === 31;
}

// Tæller BSM's ferie-lukkedage (kun hverdage) i perioden [start, end] og hvilke perioder de rammer.
// Weekender tælles IKKE med — kunder forventer ikke arbejde i weekender.
export function holidayInfo(startISO, endISO) {
  const start = toDate(startISO), end = toDate(endISO);
  if (!start || !end || end < start) return null;
  let days = 0; let sommer = false; let jul = false;
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay();
    const weekday = dow !== 0 && dow !== 6;
    if (weekday) {
      const m = cur.getMonth() + 1, day = cur.getDate();
      const isJul = (m === 12 && day >= 20) || (m === 1 && day <= 3);
      const wk = isoWeek(cur);
      const isSommer = wk === 29 || wk === 30 || wk === 31;
      if (isJul || isSommer) { days += 1; if (isSommer) sommer = true; if (isJul) jul = true; }
    }
    cur.setDate(cur.getDate() + 1);
  }
  if (!days) return null;
  return { days, sommer, jul };
}

// Menneskelig forklaring. customer=true giver kundevenlig ordlyd til delingssiden.
export function holidayLabel(info, { customer = false } = {}) {
  if (!info) return "";
  const parts = [];
  if (info.sommer) parts.push("sommerferie (uge 29–31)");
  if (info.jul) parts.push("jul- og nytårslukket (20. dec–3. jan)");
  const reason = parts.join(" + ");
  const d = `${info.days} ${info.days === 1 ? "feriedag" : "feriedage"}`;
  return customer
    ? `Perioden inkluderer BSM's ${reason}`
    : `Forlænget med ${d} — ${reason}`;
}

// Sammenhængende ferie-lukkeperioder (KUN ferie, ikke weekender) i intervallet [minMs, maxMs].
// Returnerer segmenter i dag-enheder: { offset (dage fra min), len (antal dage) }.
export function holidaySegments(minMs, maxMs) {
  if (minMs == null || maxMs == null) return [];
  const DAY = 86400000;
  const totalDays = Math.max(0, Math.round((maxMs - minMs) / DAY));
  const segs = []; let cur = null;
  for (let i = 0; i <= totalDays; i++) {
    const d = new Date(minMs + i * DAY);
    const m = d.getMonth() + 1, day = d.getDate();
    const jul = (m === 12 && day >= 20) || (m === 1 && day <= 3);
    const wk = isoWeek(d);
    const closed = jul || wk === 29 || wk === 30 || wk === 31;
    if (closed) {
      if (cur) cur.len += 1;
      else { cur = { offset: i, len: 1, label: jul ? "Jul & nytår" : "Sommerferie" }; segs.push(cur); }
    } else cur = null;
  }
  return segs;
}

// Deler en bjælke [startMs, endMs] i stykker der IKKE ligger i ferie (weekender bevares som del af bjælken).
// Returnerer dag-enheder: { offset (dage fra minMs), len }.
export function barPieces(startMs, endMs, minMs) {
  const DAY = 86400000;
  const s = Math.round((startMs - minMs) / DAY);
  const e = Math.round((endMs - minMs) / DAY);
  const pieces = []; let cur = null;
  for (let i = s; i <= e; i++) {
    const d = new Date(minMs + i * DAY);
    const m = d.getMonth() + 1, day = d.getDate();
    const jul = (m === 12 && day >= 20) || (m === 1 && day <= 3);
    const wk = isoWeek(d);
    const holiday = jul || wk === 29 || wk === 30 || wk === 31;
    if (holiday) { cur = null; }
    else if (cur) cur.len += 1; else { cur = { offset: i, len: 1 }; pieces.push(cur); }
  }
  return pieces.length ? pieces : [{ offset: s, len: Math.max(1, e - s) }];
}
